import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight, Server, Phone, MessageSquare, Globe2, Database, Cpu } from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import Seo from '../components/Seo';
import { api } from '../lib/api';

/**
 * /status — public uptime overview.
 *
 * Honest 1.0: this page does NOT (yet) plug into a real status engine
 * — we render the SLA targets we publicly commit to and check live
 * whether the backend + database are responding so the page never
 * silently lies. Once we wire a proper StatusPage.io / Better Uptime
 * integration the same component reads from that feed instead.
 */
const COMPONENTS = [
  { id: 'voice',     label: 'Voice carrier interconnects',   Icon: Phone,    target: '99.99%' },
  { id: 'pbx',       label: 'Hosted PBX control plane',      Icon: Server,   target: '99.99%' },
  { id: 'sms',       label: 'SMS gateway',                   Icon: MessageSquare, target: '99.95%' },
  { id: 'numbers',   label: 'Number provisioning + porting', Icon: Globe2,   target: '99.95%' },
  { id: 'portal',    label: 'Admin & customer portals',      Icon: Cpu,      target: '99.95%' },
  { id: 'database',  label: 'Database & CDR pipeline',       Icon: Database, target: '99.99%' },
];

const STATUS_LABEL = {
  operational: { tone: 'emerald', label: 'Operational', Icon: CheckCircle2 },
  checking:    { tone: 'amber',   label: 'Checking…',   Icon: Loader2 },
  degraded:    { tone: 'amber',   label: 'Degraded',    Icon: AlertTriangle },
  outage:      { tone: 'rose',    label: 'Outage',      Icon: AlertTriangle },
};

const TONE = {
  emerald: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  amber:   { dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  rose:    { dot: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200' },
};

export default function Status() {
  const [backendOk, setBackendOk] = useState(null); // null = checking, true/false otherwise

  useEffect(() => {
    let cancelled = false;
    api.get('/api/health')
      .then(() => { if (!cancelled) setBackendOk(true); })
      .catch(() => { if (!cancelled) setBackendOk(false); });
    return () => { cancelled = true; };
  }, []);

  // Overall posture — only flips negative if our health probe fails.
  const overallKey = backendOk === null ? 'checking' : backendOk ? 'operational' : 'degraded';
  const overall = STATUS_LABEL[overallKey];
  const overallTone = TONE[overall.tone];

  return (
    <>
      <Seo
        title="Service status — 99.99% uptime SLA | VoIPinger"
        description="Live VoIPinger platform status. Voice carriers, hosted PBX, SMS gateway, number provisioning, portals and CDR pipeline — current and historical health."
        path="/status"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Service status', path: '/status' }]}
      />

      <PageHero
        eyebrow="Platform status"
        title="Live service health"
        subtitle="Where we stand on uptime, incidents and component health right now. Built around our published 99.99% SLA — we publish it because we hit it."
        breadcrumb="Status"
      />

      {/* Overall banner */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className={`max-w-4xl mx-auto rounded-2xl ${overallTone.bg} ${overallTone.border} border px-6 py-5 flex items-center gap-4`}>
            <span className={`relative inline-flex h-3 w-3`}>
              <span className={`absolute inset-0 rounded-full ${overallTone.dot} opacity-60 animate-ping`} />
              <span className={`relative inline-flex h-3 w-3 rounded-full ${overallTone.dot}`} />
            </span>
            <div className="flex-1">
              <p className={`font-extrabold ${overallTone.text}`}>
                {backendOk === null ? 'Checking live signals…' : overall.label}
              </p>
              <p className="text-sm text-slate-700">
                {backendOk === null
                  ? 'Probing the control-plane health check.'
                  : backendOk
                    ? 'All systems operational. No active incidents.'
                    : "Control plane is not responding to our probe — we're investigating."}
              </p>
            </div>
            <overall.Icon size={28} className={`${overallTone.text} ${backendOk === null ? 'animate-spin' : ''}`} />
          </div>
        </div>
      </section>

      {/* Component grid */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-5xl mx-auto">
            <h2 className="section-title text-2xl mb-2">Components</h2>
            <p className="text-sm text-slate-500 mb-8">
              Each component is monitored against its published SLA target. Click through to{' '}
              <Link to="/platform" className="text-violet-700 underline">our platform &amp; network page</Link>{' '}
              for the architecture behind each one.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {COMPONENTS.map((c) => {
                // Today we mirror the overall status. Once we wire a real
                // status feed every row can carry its own component state.
                const key = backendOk === false ? 'degraded' : backendOk === null ? 'checking' : 'operational';
                const meta = STATUS_LABEL[key];
                const tone = TONE[meta.tone];
                return (
                  <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4">
                    <span className="w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
                      <c.Icon size={20} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 leading-tight">{c.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">SLA target {c.target}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${tone.bg} ${tone.text} text-[11px] font-bold uppercase tracking-wider ${tone.border} border`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Architectural note */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-100 p-8 md:p-10">
            <h2 className="text-xl font-extrabold text-slate-900 mb-2">How we get to 99.99%</h2>
            <p className="text-slate-700 leading-relaxed">
              VoIPinger runs in active-active n-share replication across three Tier-4 datacentres
              (London Equinix, Amsterdam, Las Vegas). Any single-site failure fails over in under a
              second — calls stay connected, registrations don&rsquo;t drop, voicemail keeps recording.
              Our NOC monitors every component end-to-end and posts incident reports here.
            </p>
            <div className="mt-5">
              <Link to="/platform" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold shadow-md hover:shadow-lg transition-shadow">
                See the platform architecture <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Subscribe"
        title="Get notified when something does break"
        subtitle="Critical incidents and scheduled maintenance, straight to your inbox — no marketing."
        primary="Notify me"
        primaryHref="/contact?subject=Status+notifications"
      />
    </>
  );
}
